import { z } from "zod";

import {
  accessLinkModes,
  adminUserRoles,
  adminUserStatuses,
  authMethods,
  bondsRequiringAccessWindow,
  brazilianStates,
  capabilities,
  councilTypes,
  deliveryStatuses,
  deployEnvironments,
  employmentBonds,
  environments,
  notificationEventTypes,
  permissions,
  professionalCategories,
  professionalCategoryInfo,
  recognitionStatuses,
  accessDirections,
  registrationAlertReasons,
  registrationAlertResolutions,
  registrationLanguages,
  registrationStatuses,
  roles,
  roomStates,
  scanActions,
  stayEntrySources,
  stayExitSources,
  subscriptionPlans
} from "./enums";
import { isValidCpf, normalizeBrazilianPhone, onlyDigits } from "./validators";

// Runtime schemas for the canonical enums.
export const roleSchema = z.enum(roles);
export const permissionSchema = z.enum(permissions);
export const capabilitySchema = z.enum(capabilities);
export const subscriptionPlanSchema = z.enum(subscriptionPlans);
export const roomStateSchema = z.enum(roomStates);
export const authMethodSchema = z.enum(authMethods);
export const environmentSchema = z.enum(environments);
export const deployEnvironmentSchema = z.enum(deployEnvironments);
export const recognitionStatusSchema = z.enum(recognitionStatuses);
export const accessDirectionSchema = z.enum(accessDirections);
export const professionalCategorySchema = z.enum(professionalCategories);
export const councilTypeSchema = z.enum(councilTypes);
export const employmentBondSchema = z.enum(employmentBonds);
export const brazilianStateSchema = z.enum(brazilianStates);
export const registrationLanguageSchema = z.enum(registrationLanguages);
export const notificationEventTypeSchema = z.enum(notificationEventTypes);
export const deliveryStatusSchema = z.enum(deliveryStatuses);
export const accessLinkModeSchema = z.enum(accessLinkModes);
export const scanActionSchema = z.enum(scanActions);

// Reusable primitives ---------------------------------------------------------

/** Telefone brasileiro; normalizado para E.164 no parse. */
export const brazilianPhoneSchema = z
  .string()
  .trim()
  .min(8, "Informe o telefone com DDD.")
  .transform((value, ctx) => {
    const normalized = normalizeBrazilianPhone(value);
    if (!normalized) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Telefone inválido. Use DDD + número." });
      return z.NEVER;
    }
    return normalized;
  });

/** CPF com dígitos verificadores válidos; normalizado para 11 dígitos. */
export const cpfSchema = z
  .string()
  .trim()
  .transform((value, ctx) => {
    if (!isValidCpf(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "CPF inválido." });
      return z.NEVER;
    }
    return onlyDigits(value);
  });

const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Data inválida (use AAAA-MM-DD).");
const emptyToUndefined = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((value) => (value === "" || value === null ? undefined : value), schema.optional());

// GET /api/v1/device/capabilities ---------------------------------------------
export const deviceCapabilitiesResponseSchema = z.object({
  plan: subscriptionPlanSchema,
  capabilities: z.array(capabilitySchema),
  tenantId: z.string(),
  environment: environmentSchema,
  deployEnvironment: deployEnvironmentSchema.optional()
});
export type DeviceCapabilitiesResponse = z.infer<typeof deviceCapabilitiesResponseSchema>;

// GET /api/v1/admin/me --------------------------------------------------------
export const membershipSchema = z.object({
  tenantId: z.string(),
  role: roleSchema,
  hospitalScope: z.string().nullable()
});
export type Membership = z.infer<typeof membershipSchema>;

export const adminMeResponseSchema = z.object({
  subject: z.string(),
  email: z.string(),
  displayName: z.string().nullable(),
  authMethod: authMethodSchema,
  roles: z.array(roleSchema),
  memberships: z.array(membershipSchema),
  tenantId: z.string().nullable(),
  plan: subscriptionPlanSchema.nullable(),
  capabilities: z.array(capabilitySchema),
  permissions: z.array(permissionSchema)
});
export type AdminMeResponse = z.infer<typeof adminMeResponseSchema>;

export const loginRequestSchema = z.object({
  username: z.string().min(3),
  password: z.string().min(8)
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const loginResponseSchema = z.object({
  token: z.string(),
  expiresAt: z.string(),
  subject: z.string(),
  email: z.string(),
  displayName: z.string().nullable(),
  roles: z.array(roleSchema),
  memberships: z.array(membershipSchema),
  permissions: z.array(permissionSchema)
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

// Usuários do painel e senha --------------------------------------------------
/** Senha de usuário do painel: 10 a 200 caracteres, com letras e números. */
export const adminPasswordSchema = z
  .string()
  .min(10, "A senha precisa ter pelo menos 10 caracteres.")
  .max(200, "A senha pode ter no máximo 200 caracteres.")
  .refine((value) => /[A-Za-z]/.test(value) && /\d/.test(value), "A senha precisa ter letras e números.");

export const adminUserRoleSchema = z.enum(adminUserRoles);
export const adminUserStatusSchema = z.enum(adminUserStatuses);

export const adminUserSchema = z.object({
  id: z.string(),
  username: z.string(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  role: adminUserRoleSchema.nullable(),
  status: z.enum(["active", "suspended", "revoked"]),
  hasPassword: z.boolean(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
  isSelf: z.boolean()
});
export type AdminUser = z.infer<typeof adminUserSchema>;

export const listAdminUsersResponseSchema = z.object({
  users: z.array(adminUserSchema),
  /** `false` enquanto o remetente de e-mail (Zenvia) não estiver configurado. */
  emailEnabled: z.boolean()
});
export type ListAdminUsersResponse = z.infer<typeof listAdminUsersResponseSchema>;

export const createAdminUserRequestSchema = z.object({
  username: z
    .string()
    .trim()
    .min(3, "O login precisa ter pelo menos 3 caracteres.")
    .max(60)
    .regex(/^[a-zA-Z0-9._-]+$/, "Use apenas letras, números, ponto, hífen ou sublinhado no login."),
  displayName: z.string().trim().min(2, "Informe o nome.").max(120),
  email: z.string().trim().email("E-mail inválido.").max(200),
  role: adminUserRoleSchema
});
// Sem campo de senha: o usuário novo sempre cria a própria senha pelo convite enviado ao e-mail.
export type CreateAdminUserRequest = z.infer<typeof createAdminUserRequestSchema>;

export const updateAdminUserRequestSchema = z
  .object({
    displayName: z.string().trim().min(2).max(120),
    email: z.string().trim().email("E-mail inválido.").max(200),
    role: adminUserRoleSchema,
    status: adminUserStatusSchema
  })
  .partial();
export type UpdateAdminUserRequest = z.infer<typeof updateAdminUserRequestSchema>;

export const adminUserResponseSchema = z.object({
  user: adminUserSchema,
  /** Se um e-mail (convite ou redefinição) foi enviado nesta operação. */
  emailSent: z.boolean().optional()
});
export type AdminUserResponse = z.infer<typeof adminUserResponseSchema>;

export const passwordResetRequestSchema = z.object({
  /** Login ou e-mail do usuário. */
  login: z.string().trim().min(3).max(200)
});
export type PasswordResetRequest = z.infer<typeof passwordResetRequestSchema>;

export const passwordResetConfirmSchema = z.object({
  token: z.string().trim().min(20).max(200),
  password: adminPasswordSchema
});
export type PasswordResetConfirm = z.infer<typeof passwordResetConfirmSchema>;

export const changeOwnPasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: adminPasswordSchema
});
export type ChangeOwnPasswordRequest = z.infer<typeof changeOwnPasswordRequestSchema>;

// Totem -----------------------------------------------------------------------
export const totemRoomSchema = z.object({
  id: z.string(),
  name: z.string(),
  unitId: z.string(),
  unitName: z.string(),
  sectorName: z.string()
});
export type TotemRoom = z.infer<typeof totemRoomSchema>;

export const listTotemRoomsResponseSchema = z.object({
  rooms: z.array(totemRoomSchema)
});
export type ListTotemRoomsResponse = z.infer<typeof listTotemRoomsResponseSchema>;

export const facialObservationRequestSchema = z.object({
  roomId: z.string().min(1),
  imageBase64: z.string().min(1),
  /**
   * Confirmação da saída: o token recebido em `exit_pending`, reenviado com um quadro novo
   * depois que o profissional ficou o tempo pedido (`holdMs`) diante do totem.
   */
  exitToken: z.string().min(1).max(400).optional()
});
export type FacialObservationRequest = z.infer<typeof facialObservationRequestSchema>;

export const facialAccessActions = ["entry", "exit", "exit_pending", "repeat_entry", "repeat_exit"] as const;
export const facialAccessActionSchema = z.enum(facialAccessActions);
export type FacialAccessAction = z.infer<typeof facialAccessActionSchema>;

export const facialObservationResponseSchema = z.object({
  eventId: z.string().nullable(),
  recorded: z.boolean(),
  status: z.enum(["recognized", "unrecognized", "blocked", "no_face"]),
  professionalId: z.string().nullable(),
  /** Nome de exibição do profissional (nunca CPF, conselho ou nascimento). */
  professionalName: z.string().nullable(),
  /** Rótulo da categoria profissional. */
  roleLabel: z.string().nullable(),
  similarity: z.number().nullable(),
  capturedAt: z.string().nullable(),
  /**
   * O que a leitura fez com a presença do profissional (só em `recognized`):
   * - `entry` / `exit`: entrada ou saída registrada agora;
   * - `exit_pending`: há entrada aberta; a saída só é registrada se ele ficar `holdMs` diante do totem;
   * - `repeat_entry` / `repeat_exit`: leitura repetida logo após a anterior, nada novo registrado.
   */
  action: facialAccessActionSchema.nullable().default(null),
  /** Horário da entrada em aberto (saída pendente/registrada) ou da entrada repetida. */
  enteredAt: z.string().nullable().default(null),
  /** Token para confirmar a saída (só em `exit_pending`). */
  exitToken: z.string().nullable().default(null),
  /** Tempo (ms) que o profissional precisa ficar diante do totem para confirmar a saída. */
  holdMs: z.number().int().nullable().default(null),
  /** URL assinada e de curta duração da foto de referência usada no reconhecimento. */
  referencePhotoUrl: z.string().nullable().default(null),
  requestId: z.string()
});
export type FacialObservationResponse = z.infer<typeof facialObservationResponseSchema>;

export const facialAccessEventSchema = z.object({
  id: z.string(),
  roomId: z.string(),
  roomName: z.string(),
  unitName: z.string(),
  professionalId: z.string().nullable(),
  professionalName: z.string().nullable(),
  roleLabel: z.string().nullable(),
  recognitionStatus: recognitionStatusSchema,
  similarity: z.number().nullable(),
  capturedAt: z.string(),
  photoAvailable: z.boolean(),
  /** Entrada/saída (leituras reconhecidas registradas a partir da regra de presença). */
  direction: accessDirectionSchema.nullable().default(null),
  /** Em uma saída: horário da entrada correspondente. */
  enteredAt: z.string().nullable().default(null)
});
export type FacialAccessEvent = z.infer<typeof facialAccessEventSchema>;

export const listFacialAccessEventsResponseSchema = z.object({
  events: z.array(facialAccessEventSchema)
});
export type ListFacialAccessEventsResponse = z.infer<typeof listFacialAccessEventsResponseSchema>;

/** Dados do profissional exibidos no detalhe de uma leitura facial (painel, `facial:review`). */
export const facialAccessProfessionalSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  fullName: z.string().nullable(),
  roleLabel: z.string(),
  specialty: z.string().nullable(),
  council: z.string().nullable(),
  badgeNumber: z.string().nullable(),
  employmentBond: z.string().nullable(),
  status: z.string(),
  accessValidFrom: z.string().nullable(),
  accessValidUntil: z.string().nullable(),
  facialPhotoAvailable: z.boolean()
});
export type FacialAccessProfessional = z.infer<typeof facialAccessProfessionalSchema>;

// GET /api/v1/admin/facial-access-events/:id
export const facialAccessEventDetailResponseSchema = z.object({
  event: facialAccessEventSchema.extend({ deviceId: z.string() }),
  /** `null` para rosto não reconhecido ou profissional excluído. */
  professional: facialAccessProfessionalSchema.nullable(),
  /** Leituras do mesmo profissional no mesmo dia (mais recentes primeiro). */
  sameDay: z.array(facialAccessEventSchema)
});
export type FacialAccessEventDetailResponse = z.infer<typeof facialAccessEventDetailResponseSchema>;

/** Quem está com entrada aberta (sem saída) no centro cirúrgico. */
export const facialPresenceSchema = z.object({
  professionalId: z.string(),
  professionalName: z.string(),
  roleLabel: z.string().nullable(),
  roomId: z.string(),
  roomName: z.string(),
  unitName: z.string(),
  enteredAt: z.string(),
  entryEventId: z.string()
});
export type FacialPresence = z.infer<typeof facialPresenceSchema>;

// GET /api/v1/admin/facial-presence
export const listFacialPresenceResponseSchema = z.object({
  presence: z.array(facialPresenceSchema),
  /** Entradas sem saída há mais que isso deixam de contar como presença (horas). */
  openEntryMaxHours: z.number()
});
export type ListFacialPresenceResponse = z.infer<typeof listFacialPresenceResponseSchema>;

// ---------------------------------------------------------------------------
// Corpo clínico (item 4)
// ---------------------------------------------------------------------------

export const professionalStatusSchema = z.enum(["active", "inactive"]);

/** Campos de perfil comuns à lista (CPF mascarado) e ao detalhe (CPF completo). */
const professionalBaseSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  /** Nome de exibição (social/abreviado): é o que o totem mostra. */
  displayName: z.string(),
  socialName: z.string().nullable(),
  /** Derivado do rótulo da categoria; mantido para compatibilidade com o histórico. */
  roleLabel: z.string(),
  category: professionalCategorySchema.nullable(),
  councilType: councilTypeSchema.nullable(),
  councilNumber: z.string().nullable(),
  councilState: brazilianStateSchema.nullable(),
  specialty: z.string().nullable(),
  rqe: z.string().nullable(),
  employmentBond: employmentBondSchema.nullable(),
  badgeNumber: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  hospitalId: z.string().nullable(),
  accessValidFrom: z.string().nullable(),
  accessValidUntil: z.string().nullable(),
  notes: z.string().nullable(),
  status: professionalStatusSchema,
  /** Falso para cadastros legados sem categoria/CPF/vínculo. */
  profileComplete: z.boolean(),
  hasFacialReference: z.boolean(),
  facialReferenceCreatedAt: z.string().nullable(),
  facialPhotoAvailable: z.boolean(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable()
});

/** Item de lista: CPF sempre mascarado, sem data de nascimento. */
export const professionalSchema = professionalBaseSchema.extend({
  cpfMasked: z.string().nullable()
});
export type Professional = z.infer<typeof professionalSchema>;

/** Detalhe (`GET /admin/professionals/:id`, exige `team:manage`): CPF e nascimento completos. */
export const professionalDetailSchema = professionalBaseSchema.extend({
  cpf: z.string().nullable(),
  birthDate: z.string().nullable()
});
export type ProfessionalDetail = z.infer<typeof professionalDetailSchema>;

export const listProfessionalsResponseSchema = z.object({
  professionals: z.array(professionalSchema)
});
export type ListProfessionalsResponse = z.infer<typeof listProfessionalsResponseSchema>;

export const professionalDetailResponseSchema = z.object({ professional: professionalDetailSchema });
export type ProfessionalDetailResponse = z.infer<typeof professionalDetailResponseSchema>;

const professionalInputShape = {
  fullName: z.string().trim().min(3, "Informe o nome completo.").max(200),
  socialName: emptyToUndefined(z.string().trim().min(2).max(120)),
  cpf: cpfSchema,
  birthDate: emptyToUndefined(isoDateSchema),
  category: professionalCategorySchema,
  councilType: emptyToUndefined(councilTypeSchema),
  councilNumber: emptyToUndefined(z.string().trim().min(2).max(30)),
  councilState: emptyToUndefined(brazilianStateSchema),
  specialty: emptyToUndefined(z.string().trim().min(2).max(120)),
  rqe: emptyToUndefined(z.string().trim().min(1).max(30)),
  employmentBond: employmentBondSchema,
  badgeNumber: emptyToUndefined(z.string().trim().min(1).max(60)),
  phone: brazilianPhoneSchema,
  email: emptyToUndefined(z.string().trim().email("E-mail inválido.").max(200)),
  hospitalId: z.string().min(1, "Informe a unidade."),
  accessValidFrom: emptyToUndefined(isoDateSchema),
  accessValidUntil: emptyToUndefined(isoDateSchema),
  status: professionalStatusSchema.optional(),
  notes: emptyToUndefined(z.string().trim().max(500))
};

type ProfessionalRefineInput = {
  category?: (typeof professionalCategories)[number] | undefined;
  councilType?: (typeof councilTypes)[number] | undefined;
  councilNumber?: string | undefined;
  councilState?: (typeof brazilianStates)[number] | undefined;
  employmentBond?: (typeof employmentBonds)[number] | undefined;
  accessValidFrom?: string | undefined;
  accessValidUntil?: string | undefined;
};

/** Regras condicionais do cadastro (4.5), compartilhadas por create/update. */
export function refineProfessional(value: ProfessionalRefineInput, ctx: z.RefinementCtx) {
  const info = value.category ? professionalCategoryInfo[value.category] : null;
  if (info?.councilRequired) {
    if (!value.councilType) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["councilType"], message: "Informe o conselho de classe." });
    }
    if (!value.councilNumber) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["councilNumber"], message: "Informe o nº do registro no conselho." });
    }
    if (!value.councilState) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["councilState"], message: "Informe a UF do conselho." });
    }
  }
  if ((value.councilNumber || value.councilState) && !value.councilType) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["councilType"], message: "Informe o tipo de conselho." });
  }
  const windowRequired =
    (info?.accessWindowRequired ?? false) ||
    (value.employmentBond ? bondsRequiringAccessWindow.includes(value.employmentBond) : false);
  if (windowRequired && (!value.accessValidFrom || !value.accessValidUntil)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["accessValidUntil"],
      message: "Informe o período de validade do acesso para este vínculo/categoria."
    });
  }
  if (value.accessValidFrom && value.accessValidUntil && value.accessValidUntil < value.accessValidFrom) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["accessValidUntil"],
      message: "A validade final deve ser igual ou posterior à inicial."
    });
  }
}

export const createProfessionalRequestSchema = z.object(professionalInputShape).superRefine(refineProfessional);
export type CreateProfessionalRequest = z.infer<typeof createProfessionalRequestSchema>;

/**
 * Edição: todos os campos opcionais. As regras condicionais são aplicadas pelo
 * domínio sobre o registro mesclado (atual + alterações), via `refineProfessional`.
 */
export const updateProfessionalRequestSchema = z.object({
  ...professionalInputShape,
  fullName: professionalInputShape.fullName.optional(),
  cpf: cpfSchema.optional(),
  category: professionalCategorySchema.optional(),
  employmentBond: employmentBondSchema.optional(),
  phone: brazilianPhoneSchema.optional(),
  hospitalId: z.string().min(1).optional(),
  // Permite limpar campos opcionais explicitamente com null.
  socialName: z.string().trim().min(2).max(120).nullable().optional(),
  birthDate: isoDateSchema.nullable().optional(),
  councilType: councilTypeSchema.nullable().optional(),
  councilNumber: z.string().trim().min(2).max(30).nullable().optional(),
  councilState: brazilianStateSchema.nullable().optional(),
  specialty: z.string().trim().min(2).max(120).nullable().optional(),
  rqe: z.string().trim().min(1).max(30).nullable().optional(),
  badgeNumber: z.string().trim().min(1).max(60).nullable().optional(),
  email: z.string().trim().email("E-mail inválido.").max(200).nullable().optional(),
  accessValidFrom: isoDateSchema.nullable().optional(),
  accessValidUntil: isoDateSchema.nullable().optional(),
  notes: z.string().trim().max(500).nullable().optional()
});
export type UpdateProfessionalRequest = z.infer<typeof updateProfessionalRequestSchema>;

export const createProfessionalResponseSchema = z.object({
  professional: professionalSchema
});
export type CreateProfessionalResponse = z.infer<typeof createProfessionalResponseSchema>;

export const facialReferenceResponseSchema = z.object({
  professionalId: z.string(),
  status: z.enum(["active", "revoked"]),
  createdAt: z.string(),
  provider: z.literal("aws-rekognition"),
  consentId: z.string().nullable()
});
export type FacialReferenceResponse = z.infer<typeof facialReferenceResponseSchema>;

/** Versão vigente do termo de consentimento facial (4.7). */
export const FACIAL_CONSENT_TERM_VERSION = "2026-09-25.v2";

// POST /api/v1/admin/professionals/:id/facial-reference/validate
export const facialReferenceValidationReasonSchema = z.enum([
  "face_detected",
  "face_not_detected",
  "multiple_faces",
  "low_quality"
]);
export type FacialReferenceValidationReason = z.infer<typeof facialReferenceValidationReasonSchema>;

export const facialReferenceValidationResponseSchema = z.object({
  valid: z.boolean(),
  reason: facialReferenceValidationReasonSchema,
  message: z.string(),
  faceCount: z.number().int().nonnegative()
});
export type FacialReferenceValidationResponse = z.infer<typeof facialReferenceValidationResponseSchema>;

// ---------------------------------------------------------------------------
// Org structure (Unidade / Setor / Sala) — admin CRUD with RBAC + delete impact.
// ---------------------------------------------------------------------------

export const orgStatusSchema = z.enum(["active", "inactive"]);
export type OrgStatus = z.infer<typeof orgStatusSchema>;

// Unidade (hospitals)
export const unitSchema = z.object({
  id: z.string(),
  name: z.string(),
  timezone: z.string(),
  street: z.string().nullable(),
  number: z.string().nullable(),
  complement: z.string().nullable(),
  neighborhood: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postalCode: z.string().nullable(),
  status: orgStatusSchema,
  createdAt: z.string()
});
export type Unit = z.infer<typeof unitSchema>;

export const unitAddressSchema = z.object({
  street: z.string().trim().max(200).optional(),
  number: z.string().trim().max(20).optional(),
  complement: z.string().trim().max(200).optional(),
  neighborhood: z.string().trim().max(200).optional(),
  city: z.string().trim().max(200).optional(),
  state: z.string().trim().max(2).optional(),
  postalCode: z.string().trim().max(20).optional()
});
export type UnitAddress = z.infer<typeof unitAddressSchema>;

export const createUnitRequestSchema = z.object({
  name: z.string().trim().min(2).max(200),
  timezone: z.string().trim().min(1).max(100).optional(),
  address: unitAddressSchema.optional()
});
export type CreateUnitRequest = z.infer<typeof createUnitRequestSchema>;

export const updateUnitRequestSchema = z.object({
  name: z.string().trim().min(2).max(200).optional(),
  timezone: z.string().trim().min(1).max(100).optional(),
  address: unitAddressSchema.optional(),
  status: orgStatusSchema.optional()
});
export type UpdateUnitRequest = z.infer<typeof updateUnitRequestSchema>;

export const unitResponseSchema = z.object({ unit: unitSchema });
export type UnitResponse = z.infer<typeof unitResponseSchema>;

export const listUnitsResponseSchema = z.object({ units: z.array(unitSchema) });
export type ListUnitsResponse = z.infer<typeof listUnitsResponseSchema>;

// Setor (sectors)
export const sectorSchema = z.object({
  id: z.string(),
  hospitalId: z.string(),
  name: z.string(),
  status: orgStatusSchema,
  createdAt: z.string()
});
export type Sector = z.infer<typeof sectorSchema>;

export const createSectorRequestSchema = z.object({
  name: z.string().trim().min(2).max(200)
});
export type CreateSectorRequest = z.infer<typeof createSectorRequestSchema>;

export const updateSectorRequestSchema = z.object({
  name: z.string().trim().min(2).max(200).optional(),
  status: orgStatusSchema.optional()
});
export type UpdateSectorRequest = z.infer<typeof updateSectorRequestSchema>;

export const sectorResponseSchema = z.object({ sector: sectorSchema });
export type SectorResponse = z.infer<typeof sectorResponseSchema>;

export const listSectorsResponseSchema = z.object({ sectors: z.array(sectorSchema) });
export type ListSectorsResponse = z.infer<typeof listSectorsResponseSchema>;

// Sala (surgical_rooms) — toda sala pertence a exatamente um setor (item 3).
export const roomSchema = z.object({
  id: z.string(),
  hospitalId: z.string(),
  sectorId: z.string(),
  name: z.string(),
  state: roomStateSchema,
  status: orgStatusSchema,
  version: z.number()
});
export type Room = z.infer<typeof roomSchema>;

export const createRoomRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  sectorId: z.string().min(1, "Informe o setor da sala.")
});
export type CreateRoomRequest = z.infer<typeof createRoomRequestSchema>;

export const updateRoomRequestSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  sectorId: z.string().min(1).optional(),
  status: orgStatusSchema.optional()
});
export type UpdateRoomRequest = z.infer<typeof updateRoomRequestSchema>;

export const roomResponseSchema = z.object({ room: roomSchema });
export type RoomResponse = z.infer<typeof roomResponseSchema>;

export const listRoomsResponseSchema = z.object({ rooms: z.array(roomSchema) });
export type ListRoomsResponse = z.infer<typeof listRoomsResponseSchema>;

// Delete impact (aviso prévio de impacto)
export const deleteImpactResponseSchema = z.object({
  id: z.string(),
  type: z.enum(["unit", "sector", "room"]),
  linkedCounts: z.record(z.string(), z.number()),
  deletable: z.boolean()
});
export type DeleteImpactResponse = z.infer<typeof deleteImpactResponseSchema>;

// ---------------------------------------------------------------------------
// Item 5 — entrada e saída do paciente pela pulseira + mensageria
// ---------------------------------------------------------------------------

// Links de acesso (5.4) --------------------------------------------------------
export const accessLinkSchema = z.object({
  id: z.string(),
  hospitalId: z.string(),
  hospitalName: z.string().nullable(),
  mode: accessLinkModeSchema,
  label: z.string(),
  /** Sala do link (modo Sala). `null` nos links de Recepção e nos antigos, sem sala fixa. */
  roomId: z.string().nullable(),
  roomName: z.string().nullable(),
  hasPin: z.boolean(),
  expiresAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  lastUsedAt: z.string().nullable(),
  createdAt: z.string(),
  /** Derivado: não revogado e não expirado. */
  active: z.boolean(),
  /** O endereço pode ser revelado de novo (links criados a partir desta versão). */
  revealable: z.boolean()
});
export type AccessLink = z.infer<typeof accessLinkSchema>;

export const createAccessLinkRequestSchema = z
  .object({
    hospitalId: z.string().min(1),
    mode: accessLinkModeSchema,
    /** Obrigatório no modo Sala: o link fica preso a esta sala. */
    roomId: z.string().min(1).optional(),
    /** Opcional: sem nome, usa o nome da sala (Sala) ou "Recepção". */
    label: z.string().trim().min(2).max(120).optional(),
    /** PIN numérico de 4 a 8 dígitos para ações sensíveis (óbito). */
    pin: z.string().trim().regex(/^\d{4,8}$/, "O PIN deve ter de 4 a 8 dígitos.").optional(),
    expiresAt: z.string().datetime({ offset: true }).optional()
  })
  .refine((value) => value.mode !== "room" || Boolean(value.roomId), {
    message: "Escolha a sala do link.",
    path: ["roomId"]
  });
export type CreateAccessLinkRequest = z.infer<typeof createAccessLinkRequestSchema>;

/** O token e a URL aparecem uma única vez, na criação. */
export const createAccessLinkResponseSchema = z.object({
  link: accessLinkSchema,
  token: z.string(),
  url: z.string()
});
export type CreateAccessLinkResponse = z.infer<typeof createAccessLinkResponseSchema>;

export const listAccessLinksResponseSchema = z.object({ links: z.array(accessLinkSchema) });
export type ListAccessLinksResponse = z.infer<typeof listAccessLinksResponseSchema>;

export const accessLinkResponseSchema = z.object({ link: accessLinkSchema });
export type AccessLinkResponse = z.infer<typeof accessLinkResponseSchema>;

/** Revelar o endereço de novo (Master e Administrador). */
export const revealAccessLinkResponseSchema = z.object({ link: accessLinkSchema, url: z.string() });
export type RevealAccessLinkResponse = z.infer<typeof revealAccessLinkResponseSchema>;

// Sessão de dispositivo (POST /api/v1/access/exchange)
export const accessExchangeRequestSchema = z.object({ token: z.string().min(16) });
export type AccessExchangeRequest = z.infer<typeof accessExchangeRequestSchema>;

export const deviceSessionSchema = z.object({
  token: z.string(),
  expiresAt: z.string(),
  mode: accessLinkModeSchema,
  tenantId: z.string(),
  hospitalId: z.string(),
  hospitalName: z.string(),
  linkId: z.string(),
  label: z.string(),
  pinRequired: z.boolean(),
  /** Sala fixa do link de Sala (`null` = recepção ou link antigo sem sala). */
  roomId: z.string().nullable().optional(),
  roomName: z.string().nullable().optional()
});
export type DeviceSession = z.infer<typeof deviceSessionSchema>;

// Cadastro na recepção (5.3) ----------------------------------------------------
export const registrationStatusSchema = z.enum(registrationStatuses);

export const patientRegistrationSchema = z.object({
  id: z.string(),
  hospitalId: z.string(),
  wristbandCode: z.string(),
  patientName: z.string(),
  companionPhone: z.string().nullable(),
  patientPhone: z.string().nullable(),
  language: registrationLanguageSchema,
  status: registrationStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Situação operacional derivada: aguardando entrada / em sala / saiu. */
  flow: z.enum(["waiting", "in_room", "exited"]),
  currentRoomId: z.string().nullable(),
  currentRoomName: z.string().nullable(),
  enteredAt: z.string().nullable(),
  exitedAt: z.string().nullable()
});
export type PatientRegistration = z.infer<typeof patientRegistrationSchema>;

export const createRegistrationRequestSchema = z.object({
  wristbandCode: z.string().trim().min(2).max(64),
  patientName: z.string().trim().min(3).max(200),
  companionPhone: brazilianPhoneSchema.nullable().optional(),
  patientPhone: brazilianPhoneSchema.nullable().optional(),
  language: registrationLanguageSchema.default("pt-BR")
});
export type CreateRegistrationRequest = z.infer<typeof createRegistrationRequestSchema>;

export const updateRegistrationRequestSchema = z.object({
  patientName: z.string().trim().min(3).max(200).optional(),
  companionPhone: brazilianPhoneSchema.nullable().optional(),
  patientPhone: brazilianPhoneSchema.nullable().optional(),
  language: registrationLanguageSchema.optional()
});
export type UpdateRegistrationRequest = z.infer<typeof updateRegistrationRequestSchema>;

export const registrationResponseSchema = z.object({ registration: patientRegistrationSchema });
export type RegistrationResponse = z.infer<typeof registrationResponseSchema>;

export const listRegistrationsResponseSchema = z.object({ registrations: z.array(patientRegistrationSchema) });
export type ListRegistrationsResponse = z.infer<typeof listRegistrationsResponseSchema>;

export const testMessageResponseSchema = z.object({
  queued: z.boolean(),
  outboxId: z.string().nullable(),
  message: z.string()
});
export type TestMessageResponse = z.infer<typeof testMessageResponseSchema>;

// Permanências (room_stays) -----------------------------------------------------
export const roomStaySchema = z.object({
  id: z.string(),
  hospitalId: z.string(),
  roomId: z.string(),
  roomName: z.string().nullable(),
  sectorName: z.string().nullable(),
  registrationId: z.string().nullable(),
  wristbandCode: z.string(),
  /** Nome completo só enquanto em sala e no dispositivo da sala; listas usam iniciais. */
  patientName: z.string(),
  patientInitials: z.string(),
  entrySource: z.enum(stayEntrySources),
  enteredAt: z.string(),
  exitSource: z.enum(stayExitSources).nullable(),
  exitedAt: z.string().nullable(),
  exitReason: z.string().nullable(),
  death: z.boolean(),
  protocolCode: z.string().nullable(),
  /** Minutos em sala (ao vivo quando aberta). */
  durationMinutes: z.number().nullable(),
  setupStartedAt: z.string().nullable(),
  setupFinishedAt: z.string().nullable(),
  setupMinutes: z.number().nullable(),
  companionNotified: z.boolean(),
  alertOpen: z.boolean(),
  messages: z
    .array(
      z.object({
        type: notificationEventTypeSchema,
        status: deliveryStatusSchema,
        sentAt: z.string().nullable(),
        error: z.string().nullable()
      })
    )
    .optional()
});
export type RoomStay = z.infer<typeof roomStaySchema>;

export const listStaysResponseSchema = z.object({ stays: z.array(roomStaySchema) });
export type ListStaysResponse = z.infer<typeof listStaysResponseSchema>;

export const forceExitRequestSchema = z.object({
  exitedAt: z.string().datetime({ offset: true }),
  reason: z.string().trim().min(3).max(300),
  notifyCompanion: z.boolean().default(false)
});
export type ForceExitRequest = z.infer<typeof forceExitRequestSchema>;

// Dispositivo da sala (5.8) ----------------------------------------------------
export const roomDeviceRoomSchema = z.object({
  id: z.string(),
  name: z.string(),
  unitId: z.string(),
  unitName: z.string(),
  sectorId: z.string(),
  sectorName: z.string(),
  state: roomStateSchema,
  openStay: roomStaySchema.nullable(),
  setupStartedAt: z.string().nullable(),
  serverTime: z.string()
});
export type RoomDeviceRoom = z.infer<typeof roomDeviceRoomSchema>;

export const listRoomDeviceRoomsResponseSchema = z.object({ rooms: z.array(roomDeviceRoomSchema) });
export type ListRoomDeviceRoomsResponse = z.infer<typeof listRoomDeviceRoomsResponseSchema>;

export const roomDeviceRoomResponseSchema = z.object({ room: roomDeviceRoomSchema });
export type RoomDeviceRoomResponse = z.infer<typeof roomDeviceRoomResponseSchema>;

export const scanRequestSchema = z.object({
  code: z.string().trim().min(2).max(64),
  format: z.string().trim().max(40).optional(),
  /** Entrada rápida (pulseira sem cadastro): nome completo do paciente. */
  patientName: z.string().trim().min(3).max(200).optional()
});
export type ScanRequest = z.infer<typeof scanRequestSchema>;

export const scanResponseSchema = z.object({
  action: scanActionSchema,
  stay: roomStaySchema.nullable(),
  room: roomDeviceRoomSchema,
  /** Aviso ao operador (ex.: acompanhante não será notificado). */
  warning: z.string().nullable(),
  companionWillBeNotified: z.boolean()
});
export type ScanResponse = z.infer<typeof scanResponseSchema>;

export const deathRequestSchema = z.object({
  pin: z.string().trim().regex(/^\d{4,8}$/),
  note: z.string().trim().max(300).optional()
});
export type DeathRequest = z.infer<typeof deathRequestSchema>;

export const urgencyResponseSchema = z.object({
  queued: z.boolean(),
  message: z.string(),
  nextAllowedAt: z.string().nullable()
});
export type UrgencyResponse = z.infer<typeof urgencyResponseSchema>;

// Pendências de cadastro (5.3.1) ----------------------------------------------
export const registrationAlertSchema = z.object({
  id: z.string(),
  hospitalId: z.string(),
  stayId: z.string(),
  registrationId: z.string().nullable(),
  reason: z.enum(registrationAlertReasons),
  createdAt: z.string(),
  resolvedAt: z.string().nullable(),
  resolvedBy: z.string().nullable(),
  resolution: z.enum(registrationAlertResolutions).nullable(),
  resolutionNote: z.string().nullable(),
  patientName: z.string(),
  wristbandCode: z.string(),
  roomId: z.string(),
  roomName: z.string().nullable(),
  patientInRoom: z.boolean(),
  companionPhone: z.string().nullable()
});
export type RegistrationAlert = z.infer<typeof registrationAlertSchema>;

export const listRegistrationAlertsResponseSchema = z.object({
  alerts: z.array(registrationAlertSchema),
  openCount: z.number().int().nonnegative()
});
export type ListRegistrationAlertsResponse = z.infer<typeof listRegistrationAlertsResponseSchema>;

export const resolveRegistrationAlertRequestSchema = z
  .object({
    resolution: z.enum(registrationAlertResolutions),
    patientName: z.string().trim().min(3).max(200).optional(),
    companionPhone: brazilianPhoneSchema.optional(),
    patientPhone: brazilianPhoneSchema.nullable().optional(),
    sendEntryMessageNow: z.boolean().default(true),
    note: z.string().trim().max(500).optional()
  })
  .superRefine((value, ctx) => {
    if (value.resolution === "completed" && !value.companionPhone) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["companionPhone"], message: "Informe o WhatsApp do acompanhante." });
    }
    if (value.resolution === "no_companion" && !value.note?.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["note"], message: "Informe a justificativa." });
    }
  });
export type ResolveRegistrationAlertRequest = z.infer<typeof resolveRegistrationAlertRequestSchema>;

export const registrationAlertResponseSchema = z.object({ alert: registrationAlertSchema });
export type RegistrationAlertResponse = z.infer<typeof registrationAlertResponseSchema>;

// Modelos de mensagem (5.3 / 5.6) ---------------------------------------------
export const messageTemplateSchema = z.object({
  id: z.string(),
  hospitalId: z.string().nullable(),
  sectorId: z.string().nullable(),
  eventType: notificationEventTypeSchema,
  language: registrationLanguageSchema,
  providerTemplateId: z.string(),
  /** Mapeamento campo do template → placeholder (`{paciente}`, `{unidade}`, `{sala}`, `{protocolo}`). */
  fields: z.record(z.string(), z.string()),
  enabled: z.boolean(),
  provisional: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string()
});
export type MessageTemplate = z.infer<typeof messageTemplateSchema>;

export const createMessageTemplateRequestSchema = z.object({
  hospitalId: z.string().min(1).nullable().optional(),
  sectorId: z.string().min(1).nullable().optional(),
  eventType: notificationEventTypeSchema,
  language: registrationLanguageSchema.default("pt-BR"),
  providerTemplateId: z.string().trim().min(4).max(120),
  fields: z.record(z.string(), z.string()).default({}),
  enabled: z.boolean().default(true),
  provisional: z.boolean().default(false)
});
export type CreateMessageTemplateRequest = z.infer<typeof createMessageTemplateRequestSchema>;

export const updateMessageTemplateRequestSchema = createMessageTemplateRequestSchema.partial();
export type UpdateMessageTemplateRequest = z.infer<typeof updateMessageTemplateRequestSchema>;

export const messageTemplateResponseSchema = z.object({ template: messageTemplateSchema });
export type MessageTemplateResponse = z.infer<typeof messageTemplateResponseSchema>;

export const listMessageTemplatesResponseSchema = z.object({ templates: z.array(messageTemplateSchema) });
export type ListMessageTemplatesResponse = z.infer<typeof listMessageTemplatesResponseSchema>;

// Fila de mensagens (notification_outbox) --------------------------------------
export const notificationOutboxItemSchema = z.object({
  id: z.string(),
  stayId: z.string().nullable(),
  registrationId: z.string().nullable(),
  notificationType: notificationEventTypeSchema,
  /** Destinatário mascarado (`(11) 9****-4321`). */
  toMasked: z.string().nullable(),
  patientInitials: z.string().nullable(),
  roomName: z.string().nullable(),
  scheduledFor: z.string(),
  deliveryStatus: deliveryStatusSchema,
  attempts: z.number().int().nonnegative(),
  deliveryError: z.string().nullable(),
  providerMessageId: z.string().nullable(),
  sentAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string()
});
export type NotificationOutboxItem = z.infer<typeof notificationOutboxItemSchema>;

export const listNotificationsResponseSchema = z.object({
  notifications: z.array(notificationOutboxItemSchema),
  counts: z.object({
    pending: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    sentToday: z.number().int().nonnegative()
  })
});
export type ListNotificationsResponse = z.infer<typeof listNotificationsResponseSchema>;

// Visão geral (1.5) e Salas ao vivo ---------------------------------------------
export const liveRoomSchema = z.object({
  id: z.string(),
  name: z.string(),
  unitId: z.string(),
  unitName: z.string(),
  sectorId: z.string(),
  sectorName: z.string(),
  state: roomStateSchema,
  status: orgStatusSchema,
  openStay: roomStaySchema.nullable(),
  setupStartedAt: z.string().nullable(),
  setupMinutes: z.number().nullable(),
  alertOpen: z.boolean()
});
export type LiveRoom = z.infer<typeof liveRoomSchema>;

export const listLiveRoomsResponseSchema = z.object({
  rooms: z.array(liveRoomSchema),
  serverTime: z.string()
});
export type ListLiveRoomsResponse = z.infer<typeof listLiveRoomsResponseSchema>;

export const overviewResponseSchema = z.object({
  patientsInRoom: z.number().int().nonnegative(),
  roomsInSetup: z.number().int().nonnegative(),
  entriesToday: z.number().int().nonnegative(),
  exitsToday: z.number().int().nonnegative(),
  failedMessages: z.number().int().nonnegative(),
  openAlerts: z.number().int().nonnegative(),
  blockedAccessToday: z.number().int().nonnegative(),
  recentAccess: z.array(facialAccessEventSchema),
  pendingProfessionals: z.array(
    z.object({
      id: z.string(),
      displayName: z.string(),
      roleLabel: z.string(),
      hasFacialReference: z.boolean(),
      profileComplete: z.boolean()
    })
  ),
  serverTime: z.string()
});
export type OverviewResponse = z.infer<typeof overviewResponseSchema>;

// Auditoria ---------------------------------------------------------------------
export const auditEventSchema = z.object({
  id: z.string(),
  actorId: z.string(),
  actorName: z.string().nullable(),
  action: z.string(),
  subjectType: z.string(),
  subjectId: z.string(),
  reason: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  createdAt: z.string()
});
export type AuditEvent = z.infer<typeof auditEventSchema>;

export const listAuditEventsResponseSchema = z.object({ events: z.array(auditEventSchema) });
export type ListAuditEventsResponse = z.infer<typeof listAuditEventsResponseSchema>;
